import { useState } from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { useAdminAuth } from "@/context/AdminAuthContext";
import {
  LayoutDashboard, Package, Tags, Award, Boxes, ShoppingCart, Users, Ticket,
  Star, LayoutTemplate, Image, Truck, CreditCard, BarChart3, Settings, UserCog, Plug,
  ScrollText, LogOut, Menu, X, Wrench, Percent, AlertTriangle, FileText, Undo2,
} from "lucide-react";

const NAV = [
  { to: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/admin/products", label: "Products", icon: Package },
  { to: "/admin/categories", label: "Categories", icon: Tags },
  { to: "/admin/brands", label: "Brands", icon: Award },
  { to: "/admin/inventory", label: "Inventory", icon: Boxes },
  { to: "/admin/orders", label: "Orders", icon: ShoppingCart },
  { to: "/admin/customers", label: "Customers", icon: Users },
  { to: "/admin/coupons", label: "Coupons", icon: Ticket },
  { to: "/admin/auto-discounts", label: "Auto Discounts", icon: Percent },
  { to: "/admin/reviews", label: "Reviews", icon: Star },
  { to: "/admin/services", label: "Services", icon: Wrench },
  { to: "/admin/homepage", label: "Homepage", icon: LayoutTemplate },
  { to: "/admin/banners", label: "Banners", icon: Image },
  { to: "/admin/shipping", label: "Shipping", icon: Truck },
  { to: "/admin/returns", label: "Returns", icon: Undo2 },
  { to: "/admin/billing", label: "Billing", icon: FileText },
  { to: "/admin/payments", label: "Payments", icon: CreditCard },
  { to: "/admin/integrations", label: "Integrations", icon: Plug },
  { to: "/admin/analytics", label: "Analytics", icon: BarChart3 },
  { to: "/admin/security", label: "Account & Security", icon: UserCog },
  { to: "/admin/audit-logs", label: "Audit Logs", icon: ScrollText },
  { to: "/admin/settings", label: "Settings", icon: Settings },
];

export default function AdminLayout() {
  const { adminUser, adminLogout, sessionExpired, sessionMessage } = useAdminAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const Sidebar = () => (
    <div className="w-64 bg-slate-900 text-slate-300 flex flex-col h-full">
      <div className="h-16 flex items-center gap-2 px-5 border-b border-slate-800 shrink-0">
        <span className="h-8 w-8 rounded-lg bg-amber-500 text-slate-900 grid place-items-center font-display font-bold">S</span>
        <span className="font-display font-bold text-white">Admin Portal</span>
      </div>
      <nav className="flex-1 overflow-y-auto p-3 no-scrollbar space-y-0.5">
        {NAV.map((n) => (
          <NavLink key={n.to} to={n.to} onClick={() => setOpen(false)} data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
            className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition ${isActive ? "bg-amber-500 text-slate-900" : "text-slate-300 hover:bg-slate-800 hover:text-white"}`}>
            <n.icon className="h-4.5 w-4.5" style={{ height: 18, width: 18 }} /> {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-slate-800 shrink-0">
        <div className="px-3 py-2 text-xs text-slate-400 truncate">{adminUser?.email}<span className="block text-amber-400 uppercase text-[10px] font-semibold">{adminUser?.role}</span></div>
        <button onClick={async () => { await adminLogout(); navigate("/admin/login"); }} data-testid="admin-logout-btn"
          className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm font-medium text-slate-300 hover:bg-slate-800 hover:text-white transition">
          <LogOut className="h-4 w-4" /> Logout
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-slate-100 overflow-hidden">
      <div className="hidden lg:block shrink-0"><Sidebar /></div>
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-slate-900/60" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-0 h-full"><Sidebar /></div>
        </div>
      )}
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="lg:hidden h-14 bg-slate-900 flex items-center px-4 gap-3 shrink-0">
          <button onClick={() => setOpen(true)} data-testid="admin-menu-btn"><Menu className="h-6 w-6 text-white" /></button>
          <span className="font-display font-bold text-white">Admin Portal</span>
        </div>
        {/* This device's session was rejected by the backend. Other devices stay
            signed in; the admin only has to sign in again here. */}
        {sessionExpired && (
          <div className="bg-red-600 text-white px-4 py-2.5 flex items-center gap-3 flex-wrap text-sm" role="alert" data-testid="admin-session-expired-banner">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span className="flex-1 min-w-0">{sessionMessage || "Your admin session expired on this device. Please sign in again."}</span>
            <span className="text-xs text-red-100 hidden sm:inline">Other devices stay signed in.</span>
            <button onClick={async () => { await adminLogout(); navigate("/admin/login"); }} data-testid="admin-session-expired-signin"
              className="bg-white/15 hover:bg-white/25 font-semibold px-3 py-1.5 rounded-lg">Sign in again</button>
          </div>
        )}
        {/* Independent sessions: a stale banner here must never block the portal. */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8"><Outlet /></div>
      </div>
    </div>
  );
}
